import React, { FC, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Trash2, Edit2, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { roundTo } from '@/shared/lib/number';

interface RoomTileProps {
  id: string;
  name?: string;
  index: number;
  floorArea: number;
  wallArea: number;
  cost: number;
  itemCount: number;
  density?: number; // cost per m² for visual scale (color intensity)
  dimsLabel?: string; // e.g. "3×4×2.5" for "Размеры"
  isActive?: boolean;
  onClick: () => void;
  onDuplicate: (id: string) => void;
  onDeleteRequest: (id: string) => void;
  onUpdateName?: (id: string, name: string) => void;
  // for native DnD reorder in grid
  draggable?: boolean;
  onDragStart?: (id: string) => void;
  onDragOver?: (e: React.DragEvent, id: string) => void;
  onDrop?: (e: React.DragEvent, id: string) => void;
}

export const RoomTile: FC<RoomTileProps> = ({
  id,
  name,
  index,
  floorArea,
  wallArea,
  cost,
  itemCount,
  density = 0,
  dimsLabel = '',
  isActive,
  onClick,
  onDuplicate,
  onDeleteRequest,
  onUpdateName,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
}) => {
  const { t } = useTranslation();
  const display = name || t('editor.room_default_name', 'Комната {{n}}', { n: index + 1 });

  // Inline-редактирование названия помещения (задействует onUpdateName).
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditValue(name || '');
    setEditing(true);
  };

  const commitEdit = () => {
    if (onUpdateName) onUpdateName(id, editValue.trim());
    setEditing(false);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditing(false);
    }
  };

  // Visual scale: density (cost/m²) → color accent (low=green-ish, high=red-ish via hue)
  const densityHue = Math.max(120 - Math.min(density / 30, 90), 0); // 120 green to ~30 orange/red
  const densityColor = `hsl(${densityHue}, 70%, 45%)`;
  const densityIntensity = Math.min(0.15 + (density / 200), 0.55); // subtle bg tint

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <motion.div
      whileHover={{ scale: 1.01, y: -1 }}
      whileTap={{ scale: 0.985 }}
      draggable={draggable}
      onDragStart={() => onDragStart && onDragStart(id)}
      onDragOver={(e) => onDragOver && onDragOver(e, id)}
      onDrop={(e) => onDrop && onDrop(e, id)}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={t('editor.room_tile.aria', 'Открыть комнату {{name}}. Размеры {{dims}}, стоимость {{cost}}', { 
        name: display, 
        dims: dimsLabel || '—', 
        cost: roundTo(cost, 0) 
      })}
      className={`
        group relative aspect-square w-full min-w-[128px] max-w-[180px] rounded-2xl border p-3 flex flex-col justify-between cursor-pointer select-none
        transition-all duration-150 outline-none
        ${isActive 
          ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-900/20 shadow-md shadow-blue-200/40 dark:shadow-none' 
          : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm'}
      `}
      style={!isActive ? { backgroundColor: `rgba(0,0,0,${densityIntensity * 0.08})` } : undefined}
    >
      {/* Top: name + actions (visible by default on mobile, hover on desktop) */}
      <div className="flex items-start justify-between gap-2">
        {onUpdateName && editing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={commitEdit}
            onKeyDown={handleEditKeyDown}
            placeholder={t('editor.room_name_input', 'Название комнаты')}
            aria-label={t('editor.room_name_input', 'Название комнаты')}
            className="min-w-0 flex-1 font-bold text-sm bg-transparent border-b border-blue-400 outline-none text-slate-800 dark:text-slate-100"
          />
        ) : (
          <div className="font-bold text-sm leading-tight text-slate-800 dark:text-slate-100 truncate pr-1">
            {display}
          </div>
        )}
        <div className="flex gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
          {onUpdateName && (
            editing ? (
              <button
                onClick={(e) => { e.stopPropagation(); commitEdit(); }}
                className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-md text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                aria-label={t('common.save', 'Сохранить')}
              >
                <Check size={14} />
              </button>
            ) : (
              <button
                onClick={startEdit}
                className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-md text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                aria-label={t('common.edit', 'Редактировать')}
              >
                <Edit2 size={14} />
              </button>
            )
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(id); }}
            className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-md text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
            aria-label={t('common.duplicate', 'Дублировать')}
          >
            <Copy size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDeleteRequest(id); }}
            className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-md text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
            aria-label={t('common.delete', 'Удалить')}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Center stats — exactly the minimal info requested for 2-col mobile tiles + visual scale */}
      <div className="mt-auto space-y-0.5 text-[10px]">
        {dimsLabel && (
          <div className="flex items-baseline justify-between font-mono tabular-nums text-slate-600 dark:text-slate-300">
            <span>{t('editor.room_tile.dims_label', 'Размеры')}</span>
            <span className="font-bold">{dimsLabel}</span>
          </div>
        )}
        <div className="flex items-baseline justify-between font-mono tabular-nums" style={{ color: densityColor }}>
          <span className="text-slate-500 dark:text-slate-400">{t('editor.room_tile.floor_ceiling', 'Пол/потолок')}</span>
          <span className="font-bold">{roundTo(floorArea, 1)} {t('common.unit_m2', 'м²')}</span>
        </div>
        <div className="flex items-baseline justify-between font-mono tabular-nums">
          <span className="text-slate-500 dark:text-slate-400">{t('editor.room_tile.walls', 'Стены')}</span>
          <span className="font-bold text-slate-700 dark:text-slate-200">{roundTo(wallArea, 1)} {t('common.unit_m2', 'м²')}</span>
        </div>
        <div className="flex items-baseline justify-between font-mono tabular-nums">
          <span className="text-slate-500 dark:text-slate-400">{t('editor.room_tile.cost', 'Стоимость')}</span>
          <span className="font-bold text-emerald-600 dark:text-emerald-400">{roundTo(cost, 0)}</span>
        </div>
        <div className="text-[9px] text-slate-500 dark:text-slate-400 flex justify-between pt-0.5 border-t border-slate-200/50 dark:border-slate-700/50">
          <span>{t('editor.room_tile.item_count', '{{n}} поз.', { n: itemCount })}</span>
          <span className="font-mono" style={{ color: densityColor }}>{t('editor.room_tile.density', '{{d}} /м²', { d: density })}</span>
        </div>
      </div>

      {/* Visual scale indicator (density bar at bottom) + active ring */}
      <div className="absolute bottom-1.5 left-3 right-3 h-1 rounded bg-slate-200 dark:bg-slate-700 overflow-hidden">
        <div 
          className="h-full transition-all" 
          style={{ width: `${Math.min(density / 2, 100)}%`, backgroundColor: densityColor }}
        />
      </div>

      {isActive && (
        <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-blue-500/60 pointer-events-none" />
      )}
    </motion.div>
  );
};

export default RoomTile;